import i18n from '@/utils/i18n';
import { RecommendationActionsSnapshot } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { useCallback, useMemo, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { useSubmitRecommendationActionsMutator } from '@/experiences/sales-portal/domain/pricing-agent/blocs/mutators/use-submit-recommendation-actions-mutator';

interface Params {
  /**
   * Persists any pending save-progress edits, resolving to whether they landed.
   * Publishing is blocked when they have not: on auto-accepted screens the
   * rejections ride this sync instead of the publish payload.
   */
  flushPendingSync: () => Promise<boolean>;
  taskTypeUUID: string;
}

export interface PublishRecommendations {
  /**
   * True for the whole publish — the pending-edit flush and the submit alike.
   * Row edits have to be blocked while it holds: an edit made after the payload
   * was captured lands in neither the submission nor the flushed sync, and the
   * post-publish wipe would then discard it, applying the price the user just
   * changed their mind about.
   */
  isPublishing: boolean;
  publish: (actions: RecommendationActionsSnapshot) => Promise<void>;
}

/**
 * The publish sequence for a reviewed recommendation task: persist the pending
 * edits, submit the captured payload, and drop the local edits once the backend
 * owns them.
 */
export const usePublishRecommendations = ({
  flushPendingSync,
  taskTypeUUID,
}: Params): PublishRecommendations => {
  const [isPublishing, setIsPublishing] = useState(false);
  const { showErrorToast } = useExperienceToasts();
  const { submitActions } = useSubmitRecommendationActionsMutator({
    taskTypeUUID,
  });
  const clearAllActions = useRecommendationActionsStore(
    (state) => state.clearAllActions,
  );

  const publish = useCallback(
    async (actions: RecommendationActionsSnapshot) => {
      setIsPublishing(true);
      try {
        const synced = await flushPendingSync();
        if (!synced) {
          showErrorToast(i18n.t('pricing.agent.publish.sync_failed'));
          return;
        }
        const succeeded = await submitActions(actions);
        if (succeeded) {
          clearAllActions();
        }
      } finally {
        setIsPublishing(false);
      }
    },
    [clearAllActions, flushPendingSync, showErrorToast, submitActions],
  );

  return useMemo(() => ({ isPublishing, publish }), [isPublishing, publish]);
};
